import React, { useState } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { cn } from '../../lib/utils';

export function HoaReserveModelerTool() {
  const [currentBalance, setCurrentBalance] = useState('50000');
  const [annualContribution, setAnnualContribution] = useState('15000');
  const [interestRate, setInterestRate] = useState('3');
  
  const [majorProjectCost, setMajorProjectCost] = useState('250000');
  const [yearsToProject, setYearsToProject] = useState('10');

  const curr = parseFloat(currentBalance) || 0;
  const ann = parseFloat(annualContribution) || 0;
  const rate = parseFloat(interestRate) || 0;
  
  const proj = parseFloat(majorProjectCost) || 0;
  const yrs = parseInt(yearsToProject) || 1;

  // FV = P(1+r/n)^(nt) + PMT * [ ((1+r/n)^(nt) - 1) / (r/n) ]
  // Simplifying to annual compounding
  const r = rate / 100;
  const futurePrincipal = curr * Math.pow(1 + r, yrs);
  const futureContributions = r > 0 ? ann * ((Math.pow(1 + r, yrs) - 1) / r) : ann * yrs;
  const totalFutureVal = futurePrincipal + futureContributions;

  // Required annual contribution to meet project:
  const shortfallAtTarget = proj - totalFutureVal;
  const requiredAnn = r > 0 ? (proj - futurePrincipal) / ((Math.pow(1 + r, yrs) - 1) / r) : (proj - curr) / yrs;

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Current Reserve Status</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Current Balance ($)</label>
              <Input type="number" value={currentBalance} onChange={e => setCurrentBalance(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Annual Contribution ($)</label>
              <Input type="number" value={annualContribution} onChange={e => setAnnualContribution(e.target.value)} />
            </div>
          </div>
          <div>
             <label className="text-[10px] uppercase font-bold text-gray-400">Est. Growth Rate / Yield (%)</label>
             <Input type="number" value={interestRate} onChange={e => setInterestRate(e.target.value)} step="0.1" />
          </div>
        </Card>
        
        <Card className="p-6 space-y-4 border-blue-100 bg-blue-50/50 dark:border-blue-900/50 dark:bg-blue-950/10">
          <h3 className="text-lg font-bold">Target Capital Project</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Projected Cost ($)</label>
              <Input type="number" value={majorProjectCost} onChange={e => setMajorProjectCost(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Years until needed</label>
              <Input type="number" value={yearsToProject} onChange={e => setYearsToProject(e.target.value)} />
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className={`p-6 flex flex-col justify-center h-full dark:bg-opacity-20 ${shortfallAtTarget > 0 ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
            <span className={`text-[10px] uppercase font-bold block mb-2 tracking-widest ${shortfallAtTarget > 0 ? 'text-amber-600' : 'text-blue-600'}`}>Est. Balance at Year {yrs}</span>
            <p className={`text-5xl font-black font-mono mb-2 tracking-tighter ${shortfallAtTarget > 0 ? 'text-amber-700' : 'text-blue-700'}`}>${Math.round(totalFutureVal).toLocaleString()}</p>
            
            {shortfallAtTarget > 0 ? (
               <div className="mt-4 p-3 bg-white/60 dark:bg-black/20 rounded-lg border border-amber-200">
                  <span className="text-xs font-bold text-amber-800">Projected Shortfall: ${Math.round(shortfallAtTarget).toLocaleString()}</span>
                  <p className="text-xs mt-1 text-amber-700/80 hover:text-amber-900">
                     Increase annual contributions to <strong>${Math.ceil(requiredAnn).toLocaleString()}</strong> to fully fund this project in {yrs} years.
                  </p>
               </div>
            ) : (
               <div className="mt-4 p-3 bg-white/60 dark:bg-black/20 rounded-lg border border-blue-200 text-blue-800 text-xs font-bold">
                  Funds sufficient. Surplus of ${Math.round(Math.abs(shortfallAtTarget)).toLocaleString()} projected.
               </div>
            )}
        </Card>
      </div>
    </div>
  );
}

